import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import omit from 'lodash/omit';
import capitalize from 'lodash/capitalize';
import { useSnackbar } from 'notistack';
import moment from 'moment';

import DeckGL from '@deck.gl/react';
import { FlyToInterpolator } from 'deck.gl';
import { PathLayer, ScatterplotLayer } from '@deck.gl/layers'; // GeoJsonLayer, IconLayer (carrier)
import {
  StaticMap,
  GeolocateControl,
  _MapContext as MapContext,
  Marker
} from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

import { makeStyles } from '@material-ui/core/styles';
import {
  Paper,
  Typography,
  Chip,
  Box,
  CircularProgress,
  IconButton,
  Grid
  // Button
} from '@material-ui/core';
import LocationOnRoundedIcon from '@material-ui/icons/LocationOnRounded';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';

import usePrevious from '../hooks/usePrevious';
// import MapboxSearch from './MapboxSearch';
import reverseGeocode from './reverseGeocode';
import GoogleSearch from './GoogleSearch';

const useStyles = makeStyles((theme) => ({
  mapWrapper: {
    position: 'fixed',
    top: 0,
    right: 0,
    left: 0,
    bottom: 0
  },
  geolocateStyle: {
    position: 'absolute',
    top: 4,
    left: theme.spacing(2) + 250,
    margin: theme.spacing(2),
    zIndex: 12,
    boxShadow: `${theme.shadows[2]} !important`
  },
  geolocateContainer: {
    zIndex: 11,
    '& button': {
      backgroundColor: '#fafafa', // theme.palette.background.default,
      borderRadius: theme.shape.borderRadius,
      '&:hover': {
        backgroundColor: theme.palette.action.hover
      },
      '&.mapboxgl-ctrl-icon': {
        filter: theme.palette.type === 'dark' ? 'invert(100%)' : null
      }
    }
  },
  geolocateButton: {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.default
  },
  loadingContainer: {
    position: 'fixed',
    left: 'calc(50% - 20px)',
    top: 'calc(50% - 20px)',
    zIndex: 1
  },
  locationMarker: {
    zIndex: 4
  }
}));

let pathsProps = {
  pickable: true,
  autoHighlight: true,
  highlightColor: [255, 101, 80, 200],
  rounded: true,
  widthScale: 20,
  widthMinPixels: 2,
  positionFormat: `XY`,
  getPath: (d) => d.path,
  getWidth: (d) => 8,
  // onHover: (info) => setHoverInfo(info),
  transitions: {
    getWidth: {
      type: 'interpolation',
      duration: 600,
      enter: (value) => [0]
    }
  }
};

let markersProps = {
  pickable: true,
  autoHighlight: true,
  stroked: true,
  filled: true,
  radiusUnits: 'pixels',
  radiusMinPixels: 1,
  radiusMaxPixels: 35,
  lineWidthMinPixels: 1,
  // onHover: (info) => setHoverInfo(info),
  getPosition: (d) => d.coordinates,
  // getRadius: (d) => (Math.sign(d.cat) === -1 ? 2 : d.cat + 3),
  getRadius: (d) => (Math.sign(d.category) === -1 ? 2 : d.category + 3),
  getFillColor: (d) => {
    switch (d.category) {
      case 1:
        return [90, 175, 250, 200];
      case 2:
        return [66, 165, 245, 200];
      case 3:
        return [33, 150, 243, 200];
      case 4:
        return [30, 136, 229, 200];
      case 5:
        return [25, 118, 210, 200];
      default:
        return [90, 175, 250, 200];
    }
  },
  getLineColor: (d) => {
    switch (d.category) {
      case 1:
        return [90, 175, 250, 200];
      case 2:
        return [66, 165, 245, 200];
      case 3:
        return [33, 150, 243, 200];
      case 4:
        return [30, 136, 229, 200];
      case 5:
        return [25, 118, 210, 200];
      default:
        return [90, 175, 250, 200];
    }
  },
  transitions: {
    getRadius: {
      type: 'spring',
      stiffness: 0.01,
      damping: 0.15,
      enter: (value) => [0] // grow from size 0
    }
  }
};

let proxyCircleProps = {
  // pickable: true,
  // autoHighlight: true,
  // highlightColor: [0, 0, 0, 10],
  stroked: true,
  filled: true,
  radiusUnits: 'meters',
  lineWidthMinPixels: 1,
  onHover: (info) => info.name,
  getPosition: (d) => d.coordinates,
  getRadius: (d) => d.miles * 1609.34,
  getFillColor: (d) => [245, 0, 87, 30],
  getLineColor: (d) => [245, 0, 87, 30],
  getTooltip: ({ object }) =>
    object && {
      html: `<h2>${object.details}</h2><div>${object.name}</div>`,
      style: {
        // backgroundColor: '#000',
        fontSize: '0.8em'
      }
    },
  transitions: {
    getRadius: {
      type: 'spring',
      stiffness: 0.01,
      damping: 0.15,
      enter: (value) => [0] // grow from size 0
    }
  }
};

const makeCirclesData = ({ lat, lng }) => {
  return [
    {
      coordinates: [lng, lat],
      miles: 50,
      name: '50 Miles',
      details: 'Tropical Storms'
    },
    {
      coordinates: [lng, lat],
      miles: 100,
      name: '100 Miles',
      details: 'Category 1 & 2'
    },
    {
      coordinates: [lng, lat],
      miles: 125,
      name: '125 Miles',
      details: 'Category 3 & 4'
    },
    {
      coordinates: [lng, lat],
      miles: 150,
      name: '150 Miles',
      details: 'Category 5'
    }
  ];
};

function DeckGLMap({
  markerMode,
  updateMarkerMode,
  dateRange,
  setEventProfiles,
  setEventYears,
  setChartData,
  highlightedPathIndex,
  setHighlightedPathIndex,
  setLocationTitle,
  darkMode
  // carrierMarkers
}) {
  const classes = useStyles();

  const [paths, setPaths] = useState([]);
  const [filteredPaths, setFilteredPaths] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [filteredMarkers, setFilteredMarkers] = useState({});
  const [location, setLocation] = useState([]);
  const [proxyCircles, setProxyCircles] = useState([]);
  const [hoverInfo, setHoverInfo] = useState();
  const [mapLoaded, setMapLoaded] = useState(false);
  const [deckLoaded, setDeckLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [clickCount, setClickCount] = useState(0);

  const mapRef = useRef(null);
  const previousDateRange = usePrevious(dateRange);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [initialViewState, setInitialViewState] = useState({
    longitude: -87,
    latitude: 32.5,
    zoom: 4.5,
    maxZoom: 15,
    minZoom: 4,
    pitch: 0,
    bearing: 0
  });

  const snackAction = (key) => (
    <IconButton onClick={() => closeSnackbar(key)}>
      <CloseRoundedIcon />
    </IconButton>
  );

  const snackbarOptions = {
    variant: 'info',
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    autoHideDuration: 5000,
    preventDuplicate: true,
    action: snackAction
  };

  useEffect(() => {
    if (mapLoaded && deckLoaded) setLoading(false);
  }, [setLoading, mapLoaded, deckLoaded]);

  useEffect(() => {
    if (dateRange !== previousDateRange && previousDateRange !== undefined) {
      let newPaths = paths.filter(
        (event) => event.year >= dateRange[0] && event.year <= dateRange[1]
      );
      setFilteredPaths(newPaths);
      let newMarkers = markers.filter(
        (marker) => marker.year >= dateRange[0] && marker.year <= dateRange[1]
      );
      setFilteredMarkers(newMarkers);
    }
  }, [dateRange, previousDateRange, paths, markers]);

  const handleResponse = ({ events, eventYears, chartData }) => {
    if (events.length < 1) {
      updateMarkerMode(true);

      return enqueueSnackbar('No Named Events found within range.', {
        variant: 'info',
        ...snackbarOptions
      });
    }
    // Line format: [{ name: string, path: [[lng, lat], [lng, lat], ...], category: int, stormid: string }, ...]
    // Marker format: [{ name: string, coordinates: [lng, lat], category: int, datetime: str }, ...]
    let lineArr = events.map(
      ({ name, linecoords, category, stormid, year }) => {
        return { name, path: linecoords, category, stormid, year };
      }
    );
    let markerArr = events.map(({ track }) => track).flat();

    setPaths(lineArr);
    setFilteredPaths(lineArr);
    setMarkers(markerArr);
    setFilteredMarkers(markerArr);

    let eventProfiles = events.map((event) => {
      return omit(event, ['linecoords', 'track']);
    });

    setEventYears(eventYears);
    setEventProfiles(eventProfiles);
    setChartData(chartData);
  };

  const handleFetchPathsError = (errorBody) => {
    updateMarkerMode(true);
    if (
      errorBody.errors &&
      errorBody.errors.length &&
      errorBody.errors[0].message
    ) {
      for (let error of errorBody.errors) {
        console.log('error message: ', error.message);
        enqueueSnackbar(error.message, {
          ...snackbarOptions,
          variant: 'error'
        });
      }
    } else {
      console.log('unrecognized error:', errorBody);
      enqueueSnackbar('Something went wrong fetching the hurricane data.', {
        ...snackbarOptions,
        variant: 'error'
      });
    }
  };

  const sendRequest = ({ lat, lng }) => {
    axios
      .get('/events', {
        params: {
          lat,
          lng
        }
      })
      .then(
        (res) => {
          handleResponse(res.data);
        },
        (err) => {
          console.log(err.response);
          handleFetchPathsError(err.response.data);
        }
      );
  };
  // TODO: keep data in case results are not found for new location
  const resetState = () => {
    updateMarkerMode(false);
    setPaths([]);
    setFilteredPaths([]);
    setMarkers([]);
    setFilteredMarkers([]);
    setProxyCircles([]);
  };

  const setupNewLocation = (lngLatObj) => {
    setLocation([lngLatObj.lng, lngLatObj.lat]);
    flyTo(lngLatObj);
    // TODO: move to after receiving results ?
    setProxyCircles(makeCirclesData(lngLatObj));
  };

  const handleClick = async (e) => {
    // event format: { x: 604, y: 256, coordinate: [lng, lat], layer: null, lngLat: [lng, lat], color: null }
    const { coordinate } = e;
    if (e.x < 75 || e.y < 75) return;
    if (!markerMode) {
      if (clickCount === 2) {
        enqueueSnackbar(
          'Use the menu in upper right corner to change marker modes if you want to set a new location.',
          snackbarOptions
        );
      }
      setClickCount(clickCount + 1);
    }
    if (!markerMode || !mapLoaded) return;
    if (!coordinate[0] || !coordinate[1]) return;

    resetState();
    let lngLat = { lng: coordinate[0], lat: coordinate[1] };
    setupNewLocation(lngLat);
    sendRequest(lngLat);
    let locationString = await reverseGeocode(lngLat);
    setLocationTitle(locationString);
  };

  const handleGeolocate = async (e) => {
    // console.log('handle geolocate called', e);
    resetState();

    let lngLat = { lng: e.coords.longitude, lat: e.coords.latitude };
    sendRequest(lngLat);
    setupNewLocation(lngLat);
    let locationString = await reverseGeocode(lngLat);
    setLocationTitle(locationString);
  };

  const getPathsFromSearch = (lngLatObj, locationText) => {
    resetState();
    setupNewLocation(lngLatObj);
    setLocationTitle(locationText);
    sendRequest(lngLatObj);
  };

  const flyTo = ({ lat, lng }) => {
    setInitialViewState({
      longitude: lng,
      latitude: lat,
      zoom: 6,
      bering: 0,
      pitch: 0,
      transitionDuration: 1000,
      transitionInterpolator: new FlyToInterpolator()
    });
  };

  return (
    <div>
      <GoogleSearch getPathsFromSearch={getPathsFromSearch} />

      {loading && (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      )}

      <div className={classes.mapWrapper}>
        <DeckGL
          initialViewState={initialViewState}
          controller={true}
          getTooltip={({ object }) => object && object.name}
          onClick={handleClick}
          ContextProvider={MapContext.Provider}
          getCursor={() => (markerMode && mapLoaded ? 'crosshair' : 'grab')}
          onLoad={() => setDeckLoaded(true)}
        >
          {deckLoaded && mapLoaded && (
            <React.Fragment>
              <ScatterplotLayer
                id='proxy-circles-layer'
                data={proxyCircles}
                visible={!markerMode ? true : false}
                {...proxyCircleProps}
              />
              <ScatterplotLayer
                id='markers-layer'
                data={filteredMarkers}
                visible={markers.length && !markerMode ? true : false}
                onHover={(info) => setHoverInfo(info)}
                {...markersProps}
              />
              <PathLayer
                id='paths-layer'
                data={filteredPaths}
                visible={paths.length && !markerMode ? true : false}
                highlightedObjectIndex={highlightedPathIndex}
                getColor={darkMode ? [255, 255, 255, 255] : [0, 0, 0, 255]}
                onHover={(info) => setHighlightedPathIndex(info.index)}
                {...pathsProps}
              />
              {/* {carrierMarkers.length > 0 && !markerMode && ( */}
              {/* <IconLayer
                id='carrier-layer'
                data={carrierMarkers}
                visible={carrierMarkers.length && !markerMode ? true : false}
                getSize={25}
                getPosition={(d) => d}
                iconAtlas='https://raw.githubusercontent.com/visgl/deck.gl-data/master/website/icon-atlas.png'
                iconMapping={{
                  marker: { x: 0, y: 0, width: 128, height: 128, mask: true }
                }}
                getIcon={(d) => 'marker'}
              /> */}
              {/* )} */}
            </React.Fragment>
          )}

          <StaticMap
            ref={mapRef}
            onLoad={() => setMapLoaded(true)}
            mapStyle={
              darkMode
                ? 'mapbox://styles/mapbox/dark-v10'
                : 'mapbox://styles/mapbox/light-v8'
            }
          >
            {location[0] && (
              <div style={{ zIndex: 1 }}>
                <Marker
                  longitude={location[0]}
                  latitude={location[1]}
                  offsetLeft={-12}
                  offsetTop={-14}
                  className={classes.locationMarker}
                >
                  <LocationOnRoundedIcon />
                </Marker>
              </div>
            )}
          </StaticMap>

          {/* https://github.com/visgl/deck.gl/issues/4691 */}
          {mapLoaded && (
            <MapContext.Consumer>
              {(outerContext) => {
                return (
                  <MapContext.Provider
                    value={{
                      map: mapRef.current ? mapRef.current.getMap() : null,
                      ...outerContext
                    }}
                  >
                    <div className={classes.geolocateContainer}>
                      <GeolocateControl
                        positionOptions={{
                          enableHighAccuracy: true,
                          timeout: 6000
                        }}
                        showUserLocation={false}
                        className={classes.geolocateStyle}
                        // style={classes.geolocateButton} // applied to button
                        onGeolocate={handleGeolocate}
                      />
                    </div>
                  </MapContext.Provider>
                );
              }}
            </MapContext.Consumer>
          )}

          {hoverInfo && hoverInfo.object && (
            <div
              style={{
                position: 'absolute',
                zIndex: 1,
                pointerEvents: 'none',
                left: hoverInfo.x,
                top: hoverInfo.y,
                minWidth: '100px',
                maxWidth: '150px'
              }}
            >
              <Paper elevation={2}>
                <Box pt={1.5} pb={1} px={1.5}>
                  <Grid container justify='space-between' alignItems='center'>
                    <Grid item xs>
                      <Box
                        fontSize={18}
                        fontWeight='fontWeightBold'
                        textAlign='left'
                        variant='body1'
                      >
                        {capitalize(hoverInfo.object.name)}
                      </Box>
                    </Grid>
                    <Grid item>
                      <Chip
                        size='small'
                        label={`Cat. ${hoverInfo.object.category}`}
                        color='primary'
                        variant='outlined'
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant='subtitle2' color='textSecondary'>
                        {moment(new Date(hoverInfo.object.datetime)).format(
                          "MMM Do, 'YY h:mm a"
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </div>
          )}
        </DeckGL>
      </div>
    </div>
  );
}

DeckGLMap.propTypes = {
  markerMode: PropTypes.bool.isRequired,
  updateMarkerMode: PropTypes.func.isRequired,
  setEventProfiles: PropTypes.func.isRequired,
  setEventYears: PropTypes.func.isRequired,
  setLocationTitle: PropTypes.func.isRequired,
  carrierMarkers: PropTypes.array
};

export default DeckGLMap;
